import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PRAYERS, MYSTERIES, suggestedMystery } from "../data/prayers";
import { Button } from "../components/ui/button";
import { Check, RotateCcw } from "lucide-react";

const DecadeCounter = () => {
  const [count, setCount] = useState(0);
  const mystery = suggestedMystery();

  return (
    <div className="mt-6 flex flex-col items-start gap-5" data-testid="decade-counter">
      <p className="font-[Cormorant_Garamond] text-lg sm:text-xl text-stone-700 italic leading-relaxed">
        Sugestão de hoje:{" "}
        <span className="not-italic text-stone-900 font-medium">{mystery}</span>
      </p>

      <div className="flex flex-wrap items-center gap-2" aria-label="Marcador das Ave Marias">
        {Array.from({ length: 10 }).map((_, i) => {
          const filled = i < count;
          return (
            <button
              type="button"
              key={i}
              onClick={() => setCount(i + 1)}
              data-testid={`bead-${i}`}
              aria-label={`Ave Maria ${i + 1}`}
              className={[
                "w-7 h-7 rounded-full border transition-all",
                filled
                  ? "bg-[#D4AF37] border-[#D4AF37] shadow-[0_0_0_4px_rgba(212,175,55,0.10)]"
                  : "bg-transparent border-stone-300 hover:border-stone-500",
              ].join(" ")}
            />
          );
        })}
      </div>

      <div className="flex items-center gap-3 pt-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setCount((c) => Math.min(10, c + 1))}
          data-testid="decade-increment"
          className="rounded-full border-stone-300 text-stone-800 hover:bg-stone-100"
        >
          <Check className="w-4 h-4 mr-1.5" /> Próxima Ave Maria
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setCount(0)}
          data-testid="decade-reset"
          className="rounded-full text-stone-500 hover:bg-stone-100"
        >
          <RotateCcw className="w-4 h-4 mr-1.5" /> Reiniciar
        </Button>
        <span className="font-[Cormorant_Garamond] text-lg text-stone-500" data-testid="decade-count">
          {count} / 10
        </span>
      </div>
    </div>
  );
};

export const PrayerCard = ({ selectedId, onSelect }) => {
  const prayer = PRAYERS.find((p) => p.id === selectedId) || PRAYERS[0];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      className="w-full"
      data-testid="prayer-card"
    >
      <div
        className="rounded-3xl backdrop-blur-xl bg-white/70 border border-white/60 shadow-[0_8px_32px_rgba(28,25,23,0.05)] p-7 md:p-10"
      >
        <div className="flex items-center justify-between mb-5">
          <p className="font-[Outfit] text-[11px] sm:text-xs tracking-[0.28em] uppercase text-stone-500">
            Momento de oração
          </p>
          <div className="hidden sm:flex w-8 h-px bg-stone-300" />
        </div>

        <h2
          className="font-[Cormorant_Garamond] text-3xl sm:text-4xl text-stone-900 mb-5 tracking-tight"
          data-testid="prayer-title"
        >
          {prayer.title}
        </h2>

        <AnimatePresence mode="wait">
          <motion.p
            key={prayer.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="font-[Cormorant_Garamond] text-lg sm:text-xl leading-relaxed text-stone-800"
            data-testid="prayer-text"
          >
            {prayer.text}
          </motion.p>
        </AnimatePresence>

        {prayer.isDecade && <DecadeCounter />}

        <div className="mt-8 flex flex-wrap gap-2" data-testid="prayer-selector">
          {PRAYERS.map((p) => {
            const active = p.id === selectedId;
            return (
              <button
                type="button"
                key={p.id}
                onClick={() => onSelect(p.id)}
                data-testid={`prayer-option-${p.id}`}
                className={[
                  "rounded-full px-4 py-2 text-xs sm:text-sm font-[Outfit] tracking-wide transition-all",
                  active
                    ? "bg-stone-900 text-stone-50 shadow-sm"
                    : "bg-transparent text-stone-700 border border-stone-300 hover:bg-stone-100",
                ].join(" ")}
              >
                {p.short}
              </button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};
